import { QUERY } from '@skooldio/classroom-frontend-api';
import { gql } from 'graphql-tag';

export const SmallCRCourseFragment = gql`
  fragment SmallCRCourseFragment on ClassroomCourse {
    id
    courseReferId
    title
    language
    totalVideoTime
    themeColor {
      primaryColor
    }
    logoUrl
  }
`;

export const CourseAllExamsFragment = gql`
  fragment CourseAllExamsFragment on ClassroomCourse {
    preTestExams: exams(type: PRETEST) {
      id
      examId
      title
      type
      isAvailable
      availableDate
    }
    postTestExams: exams(type: POSTTEST) {
      id
      examId
      title
      type
      isAvailable
      availableDate
    }
    qualificationExams: exams(type: EXAM) {
      id
      examId
      title
      type
      isAvailable
      availableDate
    }
  }
`;

const ClassroomCourseTableOfContentWithSubsectionGroupFragment = gql`
  fragment ClassroomCourseTableOfContentWithSubsectionGroupFragment on ClassroomCourseTableOfContentWithSectionGroup {
    id
    sectionGroups {
      id
      title
      referId
      contentType
      isVirtual
      sections {
        id
        title
        referId
        subSections {
          id
          title
          contentType
          referId
          canPreview
          meta {
            videoTime
          }
          releasedAt
          isLock
          metadata {
            examMode
          }
          learningModes
        }
      }
    }
  }
`;

export const ClassroomCourseTableOfContentFragment = gql`
  fragment ClassroomCourseTableOfContentFragment on ClassroomCourse {
    tableOfContent {
      id
      sections {
        id
        title
        referId
        subSections {
          id
          title
          contentType
          referId
          canPreview
          meta {
            videoTime
          }
          releasedAt
          isLock
          metadata {
            examMode
          }
          learningModes
        }
      }
    }
    tableOfContentWithSectionGroup {
      ...ClassroomCourseTableOfContentWithSubsectionGroupFragment
    }
  }
  ${ClassroomCourseTableOfContentWithSubsectionGroupFragment}
`;

export const ClassroomCourseTableOfContentTungkaiFragment = gql`
  fragment ClassroomCourseTableOfContentTungkaiFragment on ClassroomCourse {
    tableOfContentTungkai {
      id
      sections {
        id
        title
        referId
        missions {
          id
          referId
          title
          meta {
            missionType
          }
          subSections {
            id
            title
            contentType
            referId
            canPreview
            meta {
              videoTime
            }
            releasedAt
            isLock
            learningModes
            metadata {
              examMode
            }
          }
        }
      }
    }
  }
`;

export const ClassroomTimeFragment = gql`
  fragment ClassroomTimeFragment on ClassroomTime {
    classroomTimeId
    remainingTime
    isValidRemainingTime
    classroomTimeInfo
    type
  }
`;

export const ClassroomCourseInstructorFragment = gql`
  fragment ClassroomCourseInstructorFragment on ClassroomCourseInstructor {
    name
    title
    position
    longDescription
  }
`;

export const ClassroomSubsectionMetaFragment = gql`
  fragment ClassroomSubsectionMetaFragment on ClassroomCourseSubSectionContent {
    id
    title
    description
    contentType
    isComingSoon
    meta {
      videoTime
    }
    releasedAt
    isLock
  }
`;

const getClassroomCourseAttachment = gql`
  query classroomCourseAttachment($subAssetId: ID!) {
    classroomCourseAttachment(subAssetId: $subAssetId) {
      id
      title
      detail
      subType
      type
    }
  }
`;

const {
  classroom,
  classroomDownloadableCourseAssets,
  classroomSubSectionMeta,
  classroomTableOfContent,
  fillSubSectionNumber,
  classroomTime,
  getClassroomQuizSessions,
  getClassroomQuizExamPaperById,
  getClassroomQuizExamPaperResultById,
  getClassroomQuizExamPaperItemSolutionById,
  getUserAsset,
} = QUERY.Classroom;

const classroomSubSectionContent = gql`
  query classroomSubSectionContent(
    $subSectionId: ID!
    $classroomCourseId: ID!
    $subSectionReferId: ID!
  ) {
    myProfile {
      id
      displayName
      details {
        phoneNumber
      }
    }
    classroomSubSectionStatus(
      classroomId: $classroomCourseId
      courseItemReferId: $subSectionReferId
    ) {
      id
      videoProgress {
        id
        videoTime
      }
    }
    classroomSubSectionContent(subSectionId: $subSectionId, classroomCourseId: $classroomCourseId) {
      ...ClassroomSubsectionMetaFragment
      assets {
        contentAssets {
          id
          title
          referId
          description
          type
          content {
            ... on YoutubeMaterial {
              provider
              videoId
              url
              thumbnailUrl
              duration
            }
            ... on VideoMaterial {
              provider
              videoId
              url
              thumbnailUrl
              duration
              subContents {
                learningMode
                provider
                videoId
                url
                thumbnailUrl
                duration
              }
              isBonusContent
            }
            ... on LiveVideoMaterial {
              provider
              channelName
            }
            ... on ArticleMaterial {
              htmlContent
            }
            ... on ExternalLinkMaterial {
              extUrl: url
            }
            ... on QuizMaterial {
              examId
              maxAttempts
              timeLimit
              fullScore
              passingScore
              tags
              examMode
              examSource
              rawTags {
                category
                slug
                name
              }
              resultOptions {
                isShow
                showAfter
              }
              subContents {
                learningMode
                examId
                maxAttempts
                timeLimit
                fullScore
                passingScore
                tags
                examMode
                examSource
                rawTags {
                  category
                  slug
                  name
                }
                resultOptions {
                  isShow
                  showAfter
                }
              }
            }
            ... on QuizSimpleMaterial {
              items {
                id
                type
                title
                choices {
                  value
                  isAnswer
                  hint
                }
                fillInTheBlankItems {
                  answerTitle
                  gradeAnswer
                  answer
                  hint
                }
                solution {
                  value
                }
              }
            }
            ... on LiveCodingMaterial {
              language
              courseName
              solution
              sampleCode
              hint
              sct
              pec
            }
            ... on GoogleSheetLiveCodingMaterial {
              language
              spreadsheetId
              sheetId
            }
            ... on SlidesMaterial {
              slideTimeList {
                time
                slide {
                  key
                  bucket
                }
              }
            }
            ... on AssignmentMaterial {
              detail {
                type
                content
              }
              dueDate
              fileLimit
              fileSizeLimit
              submissionLimit
              fullScore
              fileTypes
              type
            }
            ... on NoteboardMaterial {
              data
            }
            ... on PDFMaterial {
              data
            }
            ... on OtherMaterial {
              data
            }
          }
        }
        materials {
          id
          referId
          title
          description
          type
          content {
            ... on OtherMaterial {
              data
            }
          }
        }
      }
    }
  }
  ${ClassroomSubsectionMetaFragment}
`;

const classroomCourse = gql`
  query classroomCourse(
    $classroomCourseId: ID!
    $classroomTimeId: ID
    $includeClassroomTime: Boolean = false
  ) {
    classroomCourse(classroomCourseId: $classroomCourseId) {
      id
      revision
      classroomMode
      currentLearningMode
      learningModes
      learningModeDetails {
        id
        name
        isActive
      }
      mode
      ...SmallCRCourseFragment
      subTitle
      sourceType
      instructors {
        ...ClassroomCourseInstructorFragment
      }
      ...ClassroomCourseTableOfContentFragment
      ...ClassroomCourseTableOfContentTungkaiFragment
      ...CourseAllExamsFragment
    }
    classroomStatus(classroomId: $classroomCourseId) {
      subSectionStatus {
        id
        subSectionStatus {
          courseItemReferId
          status
          learningModes
        }
      }
      currentProgress {
        id
        currentCourseItemReferId
      }
    }
    classroomTime(classroomTimeId: $classroomTimeId) @include(if: $includeClassroomTime) {
      ...ClassroomTimeFragment
    }
  }
  ${SmallCRCourseFragment}
  ${CourseAllExamsFragment}
  ${ClassroomTimeFragment}
  ${ClassroomCourseInstructorFragment}
  ${ClassroomCourseTableOfContentFragment}
  ${ClassroomCourseTableOfContentTungkaiFragment}
`;

// Same field as `classroomTime`, so it reads the value that query keeps up to date in the cache
export const classroomTimeStatus = gql`
  query classroomTimeStatus($classroomTimeId: ID) {
    classroomTime(classroomTimeId: $classroomTimeId) {
      isValidRemainingTime
    }
  }
`;

export {
  classroom,
  classroomCourse,
  classroomDownloadableCourseAssets,
  classroomSubSectionContent,
  classroomSubSectionMeta,
  classroomTableOfContent,
  classroomTime,
  fillSubSectionNumber,
  getClassroomCourseAttachment,
  getClassroomQuizExamPaperById,
  getClassroomQuizExamPaperItemSolutionById,
  getClassroomQuizExamPaperResultById,
  getClassroomQuizSessions,
  getUserAsset,
};
